/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { DataShareCreateBasic, DataShareScopeView } from '../../types/dataShare';
import { FormGroup, Input, Label } from 'reactstrap';
import { getDataScopeSurvey } from '../../utils/dataShare';

interface TextCounterProps {
  text: string | undefined,
  limit?: number
}

const RemainingCharactersCounter = ({ text, limit = 255 }: TextCounterProps) => {
  return <div className={'text-right'}>
    <span className={'text-ThemeTextPlaceholder'}>{limit - (text?.length ?? 0)} characters left</span>
  </div>;
};

type FormFields = Pick<DataShareCreateBasic, 'title' | 'content' | 'dataScope'>;

interface DataShareFormParams {
  form: FormFields,
  onChange: (value: Partial<DataShareCreateBasic>) => void
  disabled?: boolean,
  disabledContent?: boolean;
  titleMax?: number;
  contentMax?: number;
}

interface DataShareOption {
  label: string;
  code: DataShareScopeView;
  description?: string[];
}

const dataShareScopeViews: DataShareOption[] = [
  {
    label: 'Survey overview page',
    code: DataShareScopeView.Survey,
  },
  {
    label: 'Insights page',
    code: DataShareScopeView.Insights,
  },
  {
    label: 'Sustainability report downloads',
    code: DataShareScopeView.Downloads,
  },
  {
    label: 'Sending/receiving messages',
    code: DataShareScopeView.Messaging,
  },
];

function ViewDescription({ description }: { description: typeof dataShareScopeViews[0]['description'] }) {
  if (!description || description.length === 0) {
    return null;
  }

  return (
    <div className={'text-xs text-ThemeTextMedium'}>
      {description.map((text, index) => {
        return (<React.Fragment key={index}>{text}<br/></React.Fragment>)
      })}
    </div>
  );
}

export const DataShareForm = (props: DataShareFormParams) => {
  const { disabled, disabledContent, form, onChange, contentMax = 255 } = props;
  const labelClassName = disabled ? 'fw-bold' : '';

  const onSelectScopeView = (view: DataShareScopeView) => {
    if (!form.dataScope?.survey?.views) {
      return;
    }
    const newScopeViews = !form.dataScope.survey.views.includes(view)
      ? [...form.dataScope.survey.views, view]
      : form.dataScope.survey.views.filter((v) => v !== view);
    return onChange({ dataScope: { survey: { ...getDataScopeSurvey(form.dataScope), views: newScopeViews } } });
  };

  return (
    <div className='d-flex'>
      <fieldset className={'w-100'} disabled={disabled}>
        <div className='row'>
          <div className='col'>
            <Label><span className='strong'>Subject: </span>{form.title}</Label>
            <div className='mb-1'>
              <Input
                rows={6}
                type={'textarea'}
                maxLength={contentMax}
                required
                value={form.content}
                onChange={(e) => onChange({ content: e.target.value })}
                disabled={disabled || disabledContent}
              />
              <RemainingCharactersCounter text={form.content} limit={contentMax} />
            </div>
          </div>
        </div>
        <div>
          <Label className={labelClassName}>Access request for:</Label>


          <FormGroup check key={'data-access'}>
            <Input type='checkbox' checked={true} disabled={true} className='mr-2' />
            <Label htmlFor={'data-access'}>
              Access to all completed and non-private data
            </Label>
          </FormGroup>

          {dataShareScopeViews.map((view) => (
            <FormGroup check key={view.code}>
              <Input
                id={view.code}
                type='checkbox'
                checked={form.dataScope?.survey?.views?.includes(view.code) ?? false}
                className='mr-2'
                onChange={() => onSelectScopeView(view.code)}
              />
              <Label htmlFor={view.code}>{view.label}</Label>
              <ViewDescription description={view.description} />
            </FormGroup>
          ))}
        </div>
      </fieldset>
    </div>
  );
};
